<base-site-layout [heading]="heading() ?? (defaultHeading | translate)" [headerIcon]="icon" [loading]="loading()">
    <base-site-layout-action>
        @if (purchasingGroup(); as group) {
            @if (group.active) {
                <button
                    type="button"
                    class="btn btn-outline-danger ms-1 d-inline-block"
                    [disabled]="statusActionPending()"
                    (click)="deactivate()"
                >
                    <em class="fas fa-ban pe-1"></em>
                    {{ 'Deaktivieren' | translate }}
                </button>
            } @else {
                <button
                    type="button"
                    class="btn btn-outline-success ms-1 d-inline-block"
                    [disabled]="statusActionPending()"
                    (click)="reactivate()"
                >
                    <em class="fas fa-check pe-1"></em>
                    {{ 'Aktivieren' | translate }}
                </button>
            }
        }
    </base-site-layout-action>

    @if (purchasingGroup(); as group) {
        <div class="row justify-content-center g-3">
            <div class="col-12 col-xl-10">
                <div class="row g-3">
                    <!-- Top row: master data + members side-by-side -->
                    <div class="col-12 col-md-6">
                        <!-- Card 1: master data -->
                        <iim-card class="purchasing-group-card d-block" [header]="masterDataHeader">
                            <ng-template #masterDataHeader>
                                <div class="d-flex justify-content-between align-items-center gap-2">
                                    <h5 class="mb-0">
                                        <em class="fas fa-id-card pe-1"></em>
                                        {{ 'Stammdaten' | translate }}
                                    </h5>
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-outline-dark"
                                        (click)="openMasterData()"
                                    >
                                        <em class="fas fa-pen pe-1"></em>
                                        {{ 'Bearbeiten' | translate }}
                                    </button>
                                </div>
                            </ng-template>

                            <dl class="row g-2 mb-0 definition-list purchasing-group-definition-list">
                                <dt class="col-12 col-md-3 text-muted">{{ 'Code' | translate }}</dt>
                                <dd class="col-12 col-md-9 fw-bold text-break">{{ group.code }}</dd>

                                <dt class="col-12 col-md-3 text-muted">{{ 'Name' | translate }}</dt>
                                <dd class="col-12 col-md-9 fw-bold text-break">{{ group.name }}</dd>

                                <dt class="col-12 col-md-3 text-muted">{{ 'Status' | translate }}</dt>
                                <dd class="col-12 col-md-9">
                                    @if (group.active) {
                                        <span class="badge bg-success purchasing-group-status-badge">
                                            <em class="fas fa-check me-1"></em>
                                            {{ 'Aktiv' | translate }}
                                        </span>
                                    } @else {
                                        <span class="badge bg-secondary purchasing-group-status-badge">
                                            <em class="fas fa-pause me-1"></em>
                                            {{ 'Inaktiv' | translate }}
                                        </span>
                                    }
                                </dd>

                                <dt class="col-12 col-md-3 text-muted">{{ 'Beschreibung' | translate }}</dt>
                                <dd class="col-12 col-md-9 text-break">
                                    @if (group.description) {
                                        {{ group.description }}
                                    } @else {
                                        <span class="text-description">
                                            {{ 'Keine Beschreibung hinterlegt' | translate }}
                                        </span>
                                    }
                                </dd>
                            </dl>
                        </iim-card>
                    </div>

                    <div class="col-12 col-md-6">
                        <!-- Card 3: members -->
                        <iim-card class="purchasing-group-card d-block" [header]="membersHeader">
                            <ng-template #membersHeader>
                                <div class="d-flex justify-content-between align-items-center gap-2">
                                    <h5 class="mb-0">
                                        <em class="fas fa-users pe-1"></em>
                                        {{ 'Zugewiesene Mitarbeiter' | translate }}
                                    </h5>
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-outline-dark"
                                        [disabled]="membersLoading()"
                                        (click)="openMembersDialog()"
                                    >
                                        <em class="fas fa-user-plus pe-1"></em>
                                        {{ 'Mitarbeiter verwalten' | translate }}
                                    </button>
                                </div>
                            </ng-template>

                            @if (membersLoading()) {
                                <div class="purchasing-group-members-state text-muted">
                                    {{ 'Mitarbeiter werden geladen...' | translate }}
                                </div>
                            } @else if (memberCount() === 0) {
                                <div class="purchasing-group-members-state text-muted">
                                    {{ 'Keine Mitarbeiter zugewiesen' | translate }}
                                </div>
                            } @else {
                                <div class="purchasing-group-member-list">
                                    @for (member of members(); track member.id) {
                                        <div class="purchasing-group-member-list__item">
                                            <div class="purchasing-group-member-list__identity">
                                                <em class="fas fa-user text-muted"></em>
                                                <span class="purchasing-group-member-list__name">
                                                    {{ member.displayName }}
                                                </span>
                                            </div>
                                            @if (member.active === false) {
                                                <span class="badge bg-secondary">
                                                    {{ 'Inaktiv' | translate }}
                                                </span>
                                            }
                                        </div>
                                    }
                                </div>
                            }
                        </iim-card>
                    </div>

                    <!-- Bottom row: company code / material group, full width with horizontal company-code groups -->
                    <div class="col-12">
                        <iim-card class="purchasing-group-card d-block" [header]="companyCodeMaterialGroupHeader">
                            <ng-template #companyCodeMaterialGroupHeader>
                                <div class="d-flex justify-content-between align-items-center gap-2">
                                    <h5 class="mb-0">
                                        <em class="fas fa-sitemap pe-1"></em>
                                        {{ 'Buchungskreis' | translate }} / {{ 'Warengruppe' | translate }}
                                    </h5>
                                    <div class="d-flex gap-2">
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-outline-dark"
                                            data-cy="ASSIGN_MATERIAL_GROUPS_BULK"
                                            [disabled]="assignmentsLoading() || hasNoCompanyCodes()"
                                            (click)="openBulkMaterialGroups()"
                                        >
                                            <em class="fas fa-sitemap pe-1"></em>
                                            {{ 'Warengruppe zuweisen' | translate }}
                                        </button>
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-outline-dark"
                                            [disabled]="assignmentsLoading()"
                                            (click)="openCompanyCode()"
                                        >
                                            <em class="fas fa-building pe-1"></em>
                                            {{ 'Buchungskreise zuweisen' | translate }}
                                        </button>
                                    </div>
                                </div>
                            </ng-template>

                            @if (assignmentsLoading()) {
                                <div class="purchasing-group-members-state text-muted">
                                    {{ 'Zuständigkeiten werden geladen...' | translate }}
                                </div>
                            } @else if (hasNoCompanyCodes()) {
                                <div class="purchasing-group-members-state text-muted">
                                    {{ 'Bitte zunächst Buchungskreise auf Kopfebene zuweisen.' | translate }}
                                </div>
                            } @else {
                                <div class="cc-mg-groups">
                                    @for (companyCodeGroup of companyCodeGroups(); track companyCodeGroup.companyCodeId) {
                                        <div class="cc-mg-group" [class.cc-mg-group--orphan]="companyCodeGroup.isOrphan">
                                            <div class="cc-mg-group-head">
                                                <button
                                                    type="button"
                                                    class="cc-mg-group-toggle"
                                                    [attr.aria-expanded]="isCompanyCodeGroupExpanded(companyCodeGroup.companyCodeId)"
                                                    [title]="(isCompanyCodeGroupExpanded(companyCodeGroup.companyCodeId) ? 'Einklappen' : 'Ausklappen') | translate"
                                                    (click)="toggleCompanyCodeGroup(companyCodeGroup.companyCodeId)"
                                                >
                                                    <em
                                                        class="fas"
                                                        [class.fa-chevron-down]="isCompanyCodeGroupExpanded(companyCodeGroup.companyCodeId)"
                                                        [class.fa-chevron-right]="!isCompanyCodeGroupExpanded(companyCodeGroup.companyCodeId)"
                                                    ></em>
                                                    <span
                                                        class="cc-toggle-count"
                                                        [title]="'Zugewiesene Warengruppen' | translate"
                                                    >
                                                        {{ companyCodeGroup.assignments.length }}
                                                    </span>
                                                </button>
                                                <span class="cc-icon">
                                                    <em class="fas fa-building"></em>
                                                </span>
                                                <span class="cc-code">{{ companyCodeGroup.companyCode }}</span>
                                                <span class="cc-sep">—</span>
                                                <span class="cc-name">
                                                    {{ companyCodeGroup.companyCodeName ?? companyCodeGroup.companyCode }}
                                                </span>
                                                @if (companyCodeGroup.isOrphan) {
                                                    <span
                                                        class="badge bg-warning text-dark ms-2"
                                                        [title]="
                                                            'Buchungskreis ist nicht mehr auf Kopfebene zugewiesen. Bestehende Zuordnungen können noch entfernt, aber keine neuen angelegt werden.' | translate
                                                        "
                                                    >
                                                        <em class="fas fa-triangle-exclamation pe-1"></em>
                                                        {{ 'Nicht mehr zugewiesen' | translate }}
                                                    </span>
                                                }
                                                <button
                                                    type="button"
                                                    class="btn btn-sm ms-auto"
                                                    [class.btn-outline-dark]="!companyCodeGroup.isOrphan"
                                                    [class.btn-outline-warning]="companyCodeGroup.isOrphan"
                                                    (click)="openMaterialGroups(companyCodeGroup)"
                                                >
                                                    @if (companyCodeGroup.isOrphan) {
                                                        <em class="far fa-trash-can pe-1"></em>
                                                        {{ 'Aufräumen' | translate }}
                                                    } @else {
                                                        <em class="fas fa-sitemap pe-1"></em>
                                                        {{ 'Warengruppen' | translate }}
                                                    }
                                                </button>
                                            </div>
                                            @if (isCompanyCodeGroupExpanded(companyCodeGroup.companyCodeId)) {
                                                <div class="cc-mg-group-body">
                                                    @if (companyCodeGroup.assignments.length === 0) {
                                                        <div class="cc-mg-empty">
                                                            {{ 'Keine Warengruppen zugewiesen.' | translate }}
                                                        </div>
                                                    } @else {
                                                        @for (assignment of companyCodeGroup.assignments; track assignment.id) {
                                                            <div class="mg-row">
                                                                <em class="fas fa-sitemap mg-icon text-muted"></em>
                                                                <span class="mg-code">{{ assignment.materialGroupCode }}</span>
                                                                <span class="mg-sep">—</span>
                                                                <span class="mg-name">
                                                                    {{ assignment.materialGroupName ?? assignment.materialGroupCode }}
                                                                </span>
                                                            </div>
                                                        }
                                                    }
                                                </div>
                                            }
                                        </div>
                                    }
                                </div>
                            }
                        </iim-card>
                    </div>
                </div>
            </div>
        </div>
    }
</base-site-layout>
